/* Validação e cálculo de progresso do Cadastro de Semente de Futuro.
 *
 * Lê o estado `form` e retorna:
 *   - steps: 6 etapas (Fonte/Acesso/Insumo/Classificação/Checklist/Revisão) com status
 *   - progress: 0-100% baseado em etapas concluídas
 *   - pendencies: lista de strings com o que falta
 *   - canSubmit: se está pronto para enviar à Revisão Humana
 *
 * Estados de etapa:
 *   "empty"     → cinza  (nenhum campo essencial preenchido)
 *   "progress"  → azul   (parte preenchida)
 *   "done"      → verde  (todos essenciais OK)
 *   "warning"   → amarelo (suficiente, mas com alerta metodológico)
 *   "blocked"   → vermelho (faltando obrigatório)
 */

const has = (v) => v !== undefined && v !== null && String(v).trim().length > 0;
const hasArr = (v) => Array.isArray(v) && v.length > 0;

export function computeCadastroState(form) {
  const src = form.source || {};
  const pendencies = [];
  const warnings = [];

  // ─── 1) Fonte ──────────────────────────────────────────────────────
  const fonteFields = {
    titulo: has(src.titulo),
    data: has(src.data_publicacao),
    autores: has(src.autores) || has(src.instituicao),
    tipo: has(src.tipo_fonte),
  };
  const fonteFilled = Object.values(fonteFields).filter(Boolean).length;
  let fonteState = "empty";
  if (!fonteFields.titulo) {
    if (fonteFilled > 0) {
      fonteState = "blocked";
      pendencies.push("Informe o título da fonte.");
    }
  } else if (!fonteFields.data) {
    fonteState = "progress";
    pendencies.push("Informe a data ou ano da fonte.");
  } else if (!fonteFields.autores) {
    fonteState = "progress";
    pendencies.push("Informe autores ou instituição.");
  } else {
    fonteState = "done";
  }

  // ─── 2) Acesso ─────────────────────────────────────────────────────
  const acessoFields = {
    link: has(src.link_original),
    doi: has(src.doi),
    pdf: has(form.has_pdf) || has(src.link_drive) || has(form.link_drive_excepcional),
  };
  let acessoState = "empty";
  if (acessoFields.link || acessoFields.doi || acessoFields.pdf) {
    if (!acessoFields.link && !acessoFields.doi) {
      acessoState = "warning";
      warnings.push("Informe link ou DOI da fonte.");
    } else if (!acessoFields.pdf) {
      acessoState = "warning";
      warnings.push("Anexe PDF ou link Drive (recomendado).");
    } else {
      acessoState = "done";
    }
  } else {
    acessoState = "empty";
  }

  // ─── 3) Insumo ─────────────────────────────────────────────────────
  const insumoFields = {
    titulo: has(form.titulo_insumo),
    citacao: has(form.citacao_direta),
  };
  let insumoState = "empty";
  if (!insumoFields.titulo && !insumoFields.citacao) {
    insumoState = "empty";
  } else if (!insumoFields.citacao) {
    insumoState = "blocked";
    pendencies.push("Inclua a citação direta do insumo.");
  } else if (!insumoFields.titulo) {
    insumoState = "progress";
    pendencies.push("Informe o título do insumo.");
  } else {
    insumoState = "done";
    // Warning: citação muito longa
    if ((form.citacao_direta || "").length > 3000) {
      insumoState = "warning";
      warnings.push("A citação direta está muito longa (>3000 caracteres).");
    }
  }

  // ─── 4) Classificação ──────────────────────────────────────────────
  const classFields = {
    area: has(form.area_tematica_principal),
    classificacao: has(form.classificacao_semente),
    pestel: hasArr(form.perspectiva_pestel),
    recorte: has(form.recorte_geografico),
    horizonte: has(form.horizonte_temporal),
  };
  const classFilled = Object.values(classFields).filter(Boolean).length;
  let classState = "empty";
  if (classFilled === 0) {
    classState = "empty";
  } else if (!classFields.area || !classFields.classificacao) {
    classState = "blocked";
    if (!classFields.area) pendencies.push("Selecione a área temática.");
    if (!classFields.classificacao) pendencies.push("Defina a classificação da semente.");
  } else if (!classFields.pestel) {
    classState = "warning";
    warnings.push("Selecione ao menos uma dimensão PESTEL.");
  } else if (!classFields.recorte || !classFields.horizonte) {
    classState = "progress";
    if (!classFields.recorte) pendencies.push("Defina o recorte geográfico.");
    if (!classFields.horizonte) pendencies.push("Confirme o horizonte temporal (2030+).");
  } else {
    classState = "done";
    // Warning: horizonte sem 2030+
    if (form.horizonte_temporal && !/2030|2035|2040|2045|2050/.test(form.horizonte_temporal)) {
      classState = "warning";
      warnings.push("Horizonte temporal não indica 2030+.");
    }
  }

  // ─── 5) Checklist ──────────────────────────────────────────────────
  const checklist = form.researcher_checklist || {};
  const checklistKeys = Object.keys(checklist);
  const checklistDone = checklistKeys.filter((k) => checklist[k]).length;
  let checklistState = "empty";
  if (checklistKeys.length === 0) {
    checklistState = "empty";
  } else if (checklistDone === 0) {
    checklistState = "empty";
  } else if (checklistDone < checklistKeys.length) {
    checklistState = "progress";
  } else {
    checklistState = "done";
  }

  // ─── 6) Revisão ────────────────────────────────────────────────────
  // Concluído quando todas as etapas anteriores estão done/warning
  const previous = [fonteState, acessoState, insumoState, classState];
  const allReady = previous.every((s) => s === "done" || s === "warning");
  let revisaoState = "empty";
  if (allReady && pendencies.length === 0) {
    revisaoState = "done";
  } else if (allReady) {
    revisaoState = "warning";
  } else if (pendencies.some((p) => p)) {
    revisaoState = "blocked";
  }

  const steps = [
    { id: "fonte", label: "Fonte", state: fonteState, anchor: "block-fonte" },
    { id: "acesso", label: "Acesso", state: acessoState, anchor: "block-acesso" },
    { id: "insumo", label: "Insumo", state: insumoState, anchor: "block-insumo" },
    { id: "classificacao", label: "Classificação", state: classState, anchor: "block-classificacao" },
    { id: "checklist", label: "Checklist", state: checklistState, anchor: "block-checklist" },
    { id: "revisao", label: "Revisão", state: revisaoState, anchor: "block-revisao" },
  ];

  // Progresso: cada etapa done=100%, warning=85%, progress=50%, empty=0%, blocked=0%
  const stepWeights = { done: 100, warning: 85, progress: 50, empty: 0, blocked: 0 };
  const progress = Math.round(
    steps.reduce((acc, s) => acc + (stepWeights[s.state] || 0), 0) / steps.length,
  );

  const canSubmit =
    fonteState === "done" || fonteState === "warning";
  const canSubmitFull =
    canSubmit &&
    (acessoState === "done" || acessoState === "warning" || acessoState === "empty") &&
    (insumoState === "done" || insumoState === "warning") &&
    (classState === "done" || classState === "warning");

  return {
    steps,
    progress,
    pendencies,
    warnings,
    canSubmit: canSubmitFull,
  };
}

export const STATE_META = {
  empty:    { label: "Não iniciado",          bg: "bg-slate-100 dark:bg-slate-800",     border: "border-slate-200 dark:border-slate-700",     text: "text-slate-500 dark:text-slate-400",   dot: "bg-slate-400" },
  progress: { label: "Em andamento",          bg: "bg-blue-50 dark:bg-blue-950/30",     border: "border-blue-200 dark:border-blue-800",       text: "text-blue-700 dark:text-blue-300",     dot: "bg-blue-500" },
  done:     { label: "Concluído",             bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-800", text: "text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500" },
  warning:  { label: "Atenção",               bg: "bg-amber-50 dark:bg-amber-950/30",   border: "border-amber-200 dark:border-amber-800",     text: "text-amber-700 dark:text-amber-300",   dot: "bg-amber-500" },
  blocked:  { label: "Pendente obrigatório",  bg: "bg-rose-50 dark:bg-rose-950/30",     border: "border-rose-200 dark:border-rose-800",       text: "text-rose-700 dark:text-rose-300",     dot: "bg-rose-500" },
};

export function progressBucket(p) {
  if (p >= 91) return { label: "Pronto para Revisão Humana", color: "text-emerald-600" };
  if (p >= 61) return { label: "Quase pronto", color: "text-amber-600" };
  if (p >= 26) return { label: "Em construção", color: "text-blue-600" };
  return { label: "Início do cadastro", color: "text-slate-500" };
}
